import type {
  JoinCommunityResponse,
  LeaveCommunityResponse,
} from '@wix/ambassador-members-v1-member/types';
import type { UnblockMemberResponse } from '@wix/ambassador-members-v1-member-to-member-block/types';
import { withResolvedError } from '@wix/members-area-commons-ts';
import type { FlowAPI } from '@wix/yoshi-flow-editor';

import {
  getAccountData,
  isSlugAvailable,
  joinCommunity,
  leaveCommunity,
  unblockMember,
  updateAccountData,
  updateSlug,
} from '../server/account.api';
import type {
  GetAccountDataOptions,
  MyAccountMiddlewareService as IMyAccountMiddlewareService,
  UpdateAccountDataOptions,
  UpdateAccountDataResponse,
} from '../types';
import { Experiment } from '../types';
import type { AccountData } from '../types/server';

type ErrorHandler = FlowAPI['errorHandler'];
type HttpClient = FlowAPI['httpClient'];
type Experiments = FlowAPI['experiments'];

type MyAccountMiddlewareServiceOptions = {
  httpClient: HttpClient;
  errorHandler: ErrorHandler;
  experiments: Experiments;
};

export class MyAccountMiddlewareService implements IMyAccountMiddlewareService {
  private readonly httpClient: HttpClient;
  private readonly errorHandler: ErrorHandler;
  private readonly experiments: Experiments;

  constructor({
    httpClient,
    errorHandler,
    experiments,
  }: MyAccountMiddlewareServiceOptions) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
    this.experiments = experiments;
  }
  async getAccountData(options: GetAccountDataOptions) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.get<AccountData>(
            '/_serverless/my-account-middleware/account-data',
            {
              params: options,
            },
          )
        : this.httpClient.request(getAccountData(options));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async isSlugAvailable(slug: string) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.get<boolean>(
            '/_serverless/my-account-middleware/slug/is-available',
            {
              params: { slug },
            },
          )
        : this.httpClient.request(isSlugAvailable(slug));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async updateAccountData(options: UpdateAccountDataOptions) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.post<UpdateAccountDataResponse>(
            '/_serverless/my-account-middleware/account-data/update',
            options,
          )
        : this.httpClient.request(updateAccountData(options));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async updateSlug(slug: string) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.post<string>(
            '/_serverless/my-account-middleware/slug/update',
            { slug },
          )
        : this.httpClient.request(updateSlug(slug));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async joinCommunity() {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.post<JoinCommunityResponse>(
            '/_serverless/my-account-middleware/join-community',
          )
        : this.httpClient.request(joinCommunity());

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async leaveCommunity() {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.post<LeaveCommunityResponse>(
            '/_serverless/my-account-middleware/leave-community',
          )
        : this.httpClient.request(leaveCommunity());

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async unblockMember(memberId: string) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableMyAccountMiddleware)
        ? this.httpClient.post<UnblockMemberResponse>(
            '/_serverless/my-account-middleware/unblock-member',
            { memberId },
          )
        : this.httpClient.request(unblockMember(memberId));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }
}
